import clevertap from "clevertap-web-sdk";
import Helper from "util/Helper";

/**
 * The legacy Rx screen's publish events, for Create Rx (2026-10-15).
 *
 * Bridge's legacy prescription screen (new-bridge/modules/prescription,
 * index.js and PrescriptionPanel.js) sent these around a publish, and Orbit's
 * and bridge phr's Create Rx did not:
 *
 *   event log   START_PRESCRIPTION when the screen opens, ERROR_PRESCRIPTION_*
 *               when a check blocks a publish, PUBLISH_PRESCRIPTION after one.
 *               POST /v1/events, tied together by one session id.
 *   CleverTap   RX_PUBLISH after every publish, with the patient and their
 *               plan.
 *
 * Neither is gated on the environment, as legacy's were not: off production
 * the CleverTap events go to the TEST account. RX_PUBLISH goes to bridge's A/B
 * account. It is the only one bridge starts (App.js initializeCleverTapAB; the
 * start of its own account is commented out), and clevertap-react, which the
 * legacy screen sent RX_PUBLISH through, only pushes onto that same
 * window.clevertap. Bridge starts it at app start; Orbit starts it when Create
 * Rx opens (util/cleverTapAB).
 *
 * ONLY WHAT LEGACY REALLY SENT (Nittin, 2026-10-15). So NOT here:
 *  - The 02XXAB partner's Prescription_published_new. Legacy meant to send it,
 *    but looked the member up with the PHONE number on
 *    /v1/patients/mapping/{id}, which takes a patient id (go-user-service
 *    GetClientMappingByPatientId). The lookup always failed, so the event
 *    never went out.
 *  - The WhatsApp events. api-prescription-service sends those itself, because
 *    every publish asks it to (PrescriptionService.save, `whatsappSender`).
 *  - RX_DRAFT, which marked a draft save. This screen has no draft save.
 *  - The legacy screen's other ERROR_PRESCRIPTION_* events (GENDER,
 *    MEDICINE_NAME_EMPTY, INVESTIGATION_NAME_EMPTY), whose checks this screen
 *    does not make. Each host logs the ones it does make.
 *
 * All fire-and-forget, and quiet: nothing here may throw into its caller. The
 * publish's events run inside the save's success handler, before it clears
 * "Publishing…", so a throw would make a published prescription look failed.
 * Nothing here touches React state either, so a publish that lands after the
 * doctor has left the screen still sends its events.
 */

const helper = new Helper();

/** Runs `fn`, and logs instead of throwing. */
const quietly = (what, fn) => {
  try {
    return fn();
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn(`rx events: ${what} failed`, e);
    return undefined;
  }
};

/** One event-log entry, in the legacy screen's exact shape. */
export const logRxEvent = (service, session, event) =>
  quietly(event, () => {
    if (!session) return;
    service.logEvents(
      {
        events: [
          {
            userId: session.userId,
            event,
            severity: "INFO",
            description: event,
            device: {
              appVersion: "",
              device: "Unknown",
              deviceName: navigator.appVersion,
              osVersion: "unknown",
            },
            metadata: { patientId: session.patientId, id: session.id },
            module: { service: session.app, file: "", method: "" },
          },
        ],
      },
      () => {},
      () => {}
    );
  });

/**
 * Opens an Rx session and logs START_PRESCRIPTION. `app` names the client in
 * the event log ("phr-frontend" or "bridge-frontend"); `userId` is the
 * signed-in profile's id, as legacy logged it. Answers the session that
 * logRxEvent and rxPublished take.
 *
 * It also starts the plan read RX_PUBLISH needs, on open as legacy did, so
 * that event goes out with the publish and not a round trip after it.
 */
export const startRxSession = (service, { app, userId, patientId }) => {
  const session = {
    app,
    userId,
    // A string, as legacy's route param was.
    patientId: String(patientId),
    /* Legacy's `new Date().getTime() + patientId + user.id`. The patient id was
       a route-param STRING there, so the three were joined, not added. */
    id: `${Date.now()}${patientId}${userId}`,
    plans: [],
  };
  logRxEvent(service, session, "START_PRESCRIPTION");
  quietly("the plan read", () =>
    service.planAssignments(
      patientId,
      (res) => {
        if (Array.isArray(res)) session.plans = res;
      },
      () => {}
    )
  );
  return session;
};

/**
 * A plan's YYYYMMDD date as CleverTap's "$D_<ms>", or null when there is none.
 * Legacy sent "$D_0" (1970) for a patient with no plan and "$D_NaN" for a
 * missing date; null is CleverTap's "no value".
 */
const ctDate = (yyyymmdd) => {
  const iso = helper.getDate(yyyymmdd);
  return iso ? helper.epoch(new Date(iso)) : null;
};

/**
 * Legacy's RX_PUBLISH: profile, event, logout, in one go, so that nothing else
 * can land between them. `identity` in lower case is legacy's, kept: the SDK
 * keys a login on `Identity` (case-sensitive), so this records a property and
 * logs no Zyla patient id in to the partner's account.
 */
const rxPublishEvent = (session, patient) => {
  const first = session.plans[0] || null;
  const plan = first && first.plan;
  clevertap.profile.push({ Site: { identity: session.patientId } });
  clevertap.event.push("RX_PUBLISH", {
    patientId: session.patientId,
    clientCode: patient.clientCode,
    email: patient.email,
    status: patient.status,
    phoneNo: patient.phoneno,
    planId: plan ? plan.id : null,
    programEndDate: ctDate(first && first.programEndDate),
    programStartDate: ctDate(first && first.programStartDate),
  });
  clevertap.logout();
};

/**
 * Everything after a successful publish: PUBLISH_PRESCRIPTION, then RX_PUBLISH.
 * Each is separate, so one failing does not cost the other.
 */
export const rxPublished = (service, session, patient) => {
  if (!session || !patient) return;
  logRxEvent(service, session, "PUBLISH_PRESCRIPTION");
  quietly("RX_PUBLISH", () => rxPublishEvent(session, patient));
};
